import React, { useEffect, useRef, useState } from 'react';
import { Form, Button } from 'react-bootstrap'
import { firestore } from "_utils/firebase-app";
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";

import { CustomSpinner } from '_components/atoms';

import { useSelector, useDispatch } from "react-redux";
import { resetNewDispatcherMessageStatus, selectEmployeeMessages, selectSelectedForChat, selectSendDispatcherMessageStatus, sendDispatcherMessage, setEmployeeMessages, setEmployeeMessagesToSeen } from '_redux/slices/timeline/employees';
import { selectFirebaseInfo, selectFirebaseUser } from '_redux/slices/main-screen/user';

import { IoMdSend } from "react-icons/io";
import Message from './employee-message';


const EmployeeChat = () => {

    const messagesEndRef = useRef(null);
    const previousMessageCount = useRef(0);


    const dispatch = useDispatch();

    const selectedForChat = useSelector(selectSelectedForChat);
    const employeeMessages = useSelector(selectEmployeeMessages);
    const dbUser = useSelector(selectFirebaseInfo);                                       // logged in user
    const firebaseUser = useSelector(selectFirebaseUser);
    const sendDispatcherMessageStatus = useSelector(selectSendDispatcherMessageStatus);


    const [messageText, setMessageText] = useState("");


    useEffect(() => {
        dispatch(resetNewDispatcherMessageStatus());
    }, []);


    useEffect(() => {
        if (sendDispatcherMessageStatus === "succeeded" || sendDispatcherMessageStatus === "failed") {
            dispatch(resetNewDispatcherMessageStatus());

        }

    }, [sendDispatcherMessageStatus])

    useEffect(() => {
        if (selectedForChat?.conversation?._id) {
            const empMessagesCollectionRef = collection(firestore, "employeeMessages");
            const empMessagesQuery = query(
                empMessagesCollectionRef,
                where("conversationId", "==", selectedForChat?.conversation?._id),
                orderBy("sentAt", "desc"),
                limit(50)
            );
            const unsubscribeMessages = onSnapshot(empMessagesQuery, (querySnapshot) => {
                const messages = [];
                querySnapshot.forEach((doc) => {
                    const msgData = doc.data();
                    messages.push(msgData);
                });

                dispatch(setEmployeeMessages(Array.isFullArray(messages) ? messages : []));

                const unSeenMessages = messages?.filter((msg) => msg?.status === "sent" && msg?.senderId == selectedForChat?.employee?.employeeId);
                if (Array.isFullArray(unSeenMessages)) {

                    dispatch(setEmployeeMessagesToSeen({
                        iata: dbUser.details.authority,
                        conversationId: selectedForChat?.conversation?._id ? selectedForChat?.conversation?._id : `${selectedForChat?.employee?.employeeId}_${selectedForChat?.employee?.airport}`
                    }))
                }
            });
            return () => unsubscribeMessages();
        }
    }, [selectedForChat?.conversation?._id]);

    // scroll to bottom when messages change
    useEffect(() => {
        const currentMessageCount = employeeMessages?.length || 0;
        const previousCount = previousMessageCount.current;

        // Scroll if we have messages and either:
        // 1. This is the first time messages are loaded (previousCount === 0)
        // 2. We have more messages than before (new messages arrived)
        if (employeeMessages && currentMessageCount > 0 && currentMessageCount !== previousCount) {
            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }

        // Update the ref with current count
        previousMessageCount.current = currentMessageCount;
    }, [employeeMessages]);


    const handleSendMessage = async () => {
        const payload = {

            iata: dbUser.details.authority,
            body: {
                conversationId: selectedForChat?.conversation?._id ? selectedForChat?.conversation?._id : `${selectedForChat?.employee?.employeeId}_${selectedForChat?.employee?.airport}`,
                senderId: firebaseUser?.uid,
                senderName: firebaseUser?.displayName,
                text: messageText,
                receiverId: selectedForChat?.employee?.employeeId,
                receiverName: selectedForChat?.employee?.title,
                source: "PORTAL",
                fcmToken: selectedForChat?.employee?.FCMTokens
            }

        };

        const res = await dispatch(sendDispatcherMessage(payload));

        if (res?.meta?.requestStatus === "fulfilled") {
            setMessageText("")
        }

    }

    return (
        <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            height: "100%"
        }}>

            {/* message container */}
            <div style={{
                flex: "1 1 auto",
                minHeight: "600px",
                maxHeight: "600px",
                overflowY: "scroll",
                paddingInline: "30px",
                display: "flex",
                flexDirection: "column",
                border: '1px solid #ccc',
                borderRadius: "8px"
            }} >

                {/* messages */}
                {employeeMessages && employeeMessages.length > 0 ? (
                    employeeMessages.map((msg) => {

                        /* header message */
                        if ('type' in msg && msg.type === 'header') {
                            return (
                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    margin: "10px 20px",
                                    justifyContent: "center"
                                }}>
                                    <p style={{ margin: 0, width: "fit-content" }}>{msg.date} </p>
                                </div>
                            );
                        }

                        /* message */
                        return <Message msg={msg} emp={selectedForChat?.employee} />;
                    })
                ) : (
                    /* empty state */
                    <div style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "200px",
                        color: "#666",
                        textAlign: "center"
                    }}>
                        <p style={{ margin: 0, fontSize: "16px", fontWeight: "500" }}>No messages yet</p>
                        <p style={{ margin: "8px 0 0 0", fontSize: "14px" }}>Start a conversation by sending a message below</p>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* message input + send button */}
            <div style={{
                flex: "0 0 auto",
                display: "flex",
                flexDirection: "column",
                gap: "10px"
            }}>
                <Form.Group className="d-flex" style={{ gap: "10px" }}>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        onChange={(e) => setMessageText(e.target.value)}
                        value={messageText}
                        style={{ resize: "none" }}
                    />
                    <Button
                        variant="outline-primary"
                        style={{ height: "auto", minWidth: "50px" }}
                        disabled={!messageText}
                        onClick={handleSendMessage}
                    >
                        {sendDispatcherMessageStatus === "loading" ? <CustomSpinner size={"small"} /> : <IoMdSend />}
                    </Button>
                </Form.Group>
            </div>

        </div>
    )
}

export default EmployeeChat
